import { FC, useEffect, useMemo } from "react";
import { Form, useFormikContext } from "formik";
import { Stack, useTheme } from "@mui/material";
import {
  Modal,
  FormikInputField,
  FormikSelectField,
  FormikPlacesSearchField
} from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import moment from "moment";

// Data
import { useAppDispatch, useAppSelector } from "hooks";
import {
  getAllKeysByAccountThunk,
  getGooglePlacesThunk,
  getUsersBasicInfoThunk
} from "app/features";
import {
  selectAccountKeysState,
  selectAccountsState,
  selectDashboardGooglePlacesState,
  selectDrawerSelectedAccountId
} from "app/data/selectors";
import {
  CREATE_OBJECT_RIDE_MODE_OPTIONS,
  ICreateManualRideFormValues
} from "app/data/helpers/createManualRideHelpers";
import { ObjectRideModeEnum } from "app/data/helpers/objectRideHelpers";

interface Props {
  open: boolean;
  onClose: () => void;
}

const InnerForm: FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );
  const { usersBasicInfo } = useAppSelector(selectAccountsState);
  const { googlePredictatedPlacesLoading, googlePredictatedPlaces } =
    useAppSelector(selectDashboardGooglePlacesState);

  const {
    values,
    isSubmitting,
    isValid,
    setFieldValue,
    setFieldError,
    validateForm,
    handleSubmit,
    resetForm
  } = useFormikContext<ICreateManualRideFormValues>();

  const rideModeOptions = useMemo(
    () => CREATE_OBJECT_RIDE_MODE_OPTIONS(t),
    [t]
  );

  const maxDateTime = moment().format("YYYY-MM-DDTHH:mm");
  const isMixedRide = Number(values.rideMode) === ObjectRideModeEnum.MIXED;

  // FormikSelectField already sets the selected field via setValue(onChange arg).
  // Only clear the mutually exclusive field and revalidate — do not write onChange
  // payload back into form state (it may be an event, not an id).
  const clearDriverForKeySelection = async () => {
    await setFieldValue("driverId", "", false);
    setFieldError("driverId", undefined);
    setFieldError("keyId", undefined);
    await validateForm();
  };

  const clearKeyForDriverSelection = async () => {
    await setFieldValue("keyId", "", false);
    setFieldError("driverId", undefined);
    setFieldError("keyId", undefined);
    await validateForm();
  };

  const debounceFetchPlaces = (searchedText: string) => {
    dispatch(getGooglePlacesThunk(searchedText));
  };

  useEffect(() => {
    if (!accountId) return;
    dispatch(getAllKeysByAccountThunk(accountId));
    dispatch(getUsersBasicInfoThunk(accountId));
  }, [accountId, dispatch]);

  useEffect(() => {
    if (
      !isMixedRide &&
      values.privateMileage !== "" &&
      values.privateMileage != null
    ) {
      setFieldValue("privateMileage", "");
    }
  }, [isMixedRide, setFieldValue, values.privateMileage]);

  const onCloseHandler = () => {
    onClose();
    resetForm();
  };

  return (
    <Form>
      <Modal
        open={open}
        onClose={onCloseHandler}
        title={t("addManualRideForm.title")}
        primaryBtnProps={{
          type: "submit",
          text: t("buttonsText.submit"),
          loading: isSubmitting,
          disabled: !isValid,
          // @ts-ignore
          onClick: handleSubmit
        }}
        secondaryBtnProps={{
          text: t("buttonsText.cancel"),
          onClick: onCloseHandler
        }}
        theme={theme}
      >
        <Stack spacing={2}>
          <FormikInputField
            theme={theme}
            label={t("addManualRideForm.startDate")}
            type="datetime-local"
            name="startDate"
            inputProps={{ max: maxDateTime }}
            fullWidth
            required
          />
          <FormikInputField
            theme={theme}
            label={t("addManualRideForm.endDate")}
            type="datetime-local"
            name="endDate"
            inputProps={{ max: maxDateTime }}
            fullWidth
            required
          />

          <FormikPlacesSearchField
            //@ts-ignore
            placeholder={t("typography.searchLocations")}
            name="startAddress"
            label={t("addManualRideForm.startAddress")}
            debounceFetchPlaces={debounceFetchPlaces}
            googlePredictatedPlacesLoading={googlePredictatedPlacesLoading}
            googlePredictatedPlaces={googlePredictatedPlaces}
            onChange={(_, value) => {
              setFieldValue("startLatitude", value?.lat ?? null);
              setFieldValue("startLongitude", value?.long ?? null);
              validateForm();
            }}
            loadingText={t("common.loading")}
          />

          <FormikPlacesSearchField
            //@ts-ignore
            placeholder={t("typography.searchLocations")}
            name="endAddress"
            label={t("addManualRideForm.endAddress")}
            debounceFetchPlaces={debounceFetchPlaces}
            googlePredictatedPlacesLoading={googlePredictatedPlacesLoading}
            googlePredictatedPlaces={googlePredictatedPlaces}
            onChange={(_, value) => {
              setFieldValue("endLatitude", value?.lat ?? null);
              setFieldValue("endLongitude", value?.long ?? null);
              validateForm();
            }}
            loadingText={t("common.loading")}
          />

          <FormikSelectField
            theme={theme}
            label={t("addManualRideForm.rideMode")}
            name="rideMode"
            options={rideModeOptions}
            optionLabelKey="label"
            optionValueKey="value"
            required
            fullWidth
          />

          <FormikInputField
            theme={theme}
            label={t("addManualRideForm.mileage")}
            name="mileage"
            type="number"
            inputProps={{ min: 0, step: "any" }}
            fullWidth
            required
          />

          {isMixedRide && (
            <FormikInputField
              theme={theme}
              label={t("addManualRideForm.privateMileage")}
              name="privateMileage"
              type="number"
              inputProps={{
                min: 0,
                max: values.mileage || undefined,
                step: "any"
              }}
              fullWidth
              required
            />
          )}

          <FormikSelectField
            key={`keyId-${values.driverId || "empty"}`}
            theme={theme}
            label={t("addManualRideForm.key")}
            name="keyId"
            options={accountKeys ?? []}
            optionLabelKey="label"
            optionValueKey="uniqueId"
            loading={accountKeysLoading}
            searchable
            fullWidth
            onChange={() => {
              void clearDriverForKeySelection();
            }}
            onClearText={() => {
              void (async () => {
                await setFieldValue("keyId", "", false);
                setFieldError("keyId", undefined);
                await validateForm();
              })();
            }}
          />

          <FormikSelectField
            key={`driverId-${values.keyId || "empty"}`}
            theme={theme}
            label={t("driver.label", { ns: "formFieldsTranslation" })}
            placeholder={t("driver.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="driverId"
            options={usersBasicInfo ?? []}
            optionLabelKey="email"
            optionValueKey="uniqueId"
            searchable
            fullWidth
            onChange={() => {
              void clearKeyForDriverSelection();
            }}
            onClearText={() => {
              void (async () => {
                await setFieldValue("driverId", "", false);
                setFieldError("driverId", undefined);
                await validateForm();
              })();
            }}
          />

          <FormikInputField
            theme={theme}
            label={t("addManualRideForm.comments")}
            name="comments"
            multiline
            rows={2}
            fullWidth
            placeholder={t("addManualRideForm.commentsPlaceholder")}
          />
        </Stack>
      </Modal>
    </Form>
  );
};

export default InnerForm;
